/**
 * Global Debug Logger Utility for Playground League
 * Provides lifecycle tracing, component loading diagnostics, performance profiling, and root mounting logs.
 */

export interface DebugLogEntry {
  timestamp: string;
  phase: string;
  message: string;
  details?: any;
}

declare global {
  interface Window {
    __PLAYGROUND_DEBUG__?: {
      logs: DebugLogEntry[];
      isIsolationTestActive: boolean;
      getLogs: () => DebugLogEntry[];
      clearLogs: () => void;
      moduleEvalTime?: number;
    };
  }
}

// Performance tracking timestamp for module eval -> component init trace
let appModuleEvalTimestamp: number | null = null;

// Initialize global debug container on window if in browser environment
if (typeof window !== 'undefined') {
  window.__PLAYGROUND_DEBUG__ = window.__PLAYGROUND_DEBUG__ || {
    logs: [],
    isIsolationTestActive: false,
    getLogs: () => window.__PLAYGROUND_DEBUG__?.logs || [],
    clearLogs: () => {
      if (window.__PLAYGROUND_DEBUG__) {
        window.__PLAYGROUND_DEBUG__.logs = [];
      }
    },
  };
}

export function debugLog(phase: string, message: string, details?: any) {
  const timestamp = new Date().toISOString();
  const entry: DebugLogEntry = { timestamp, phase, message, details };

  if (typeof window !== 'undefined' && window.__PLAYGROUND_DEBUG__) {
    window.__PLAYGROUND_DEBUG__.logs.push(entry);
  }

  const formattedMsg = `[Playground Debug][${phase}] ${message}`;
  if (details !== undefined) {
    console.log(formattedMsg, details);
  } else {
    console.log(formattedMsg);
  }
}

export function markModuleEval() {
  if (typeof performance !== 'undefined') {
    appModuleEvalTimestamp = performance.now();
    if (typeof window !== 'undefined' && window.__PLAYGROUND_DEBUG__) {
      window.__PLAYGROUND_DEBUG__.moduleEvalTime = appModuleEvalTimestamp;
    }
  }
  debugLog('APP_MODULE_EVAL', 'src/App.tsx module evaluation started');
}

export function markComponentInit() {
  let elapsedMs: number | null = null;
  if (typeof performance !== 'undefined') {
    const initTime = performance.now();
    const evalTime = appModuleEvalTimestamp ?? (typeof window !== 'undefined' ? window.__PLAYGROUND_DEBUG__?.moduleEvalTime : null);
    if (evalTime != null) {
      elapsedMs = parseFloat((initTime - evalTime).toFixed(2));
    }
  }

  if (elapsedMs !== null) {
    debugLog(
      'PERFORMANCE_TRACE',
      `[Performance Trace] Time elapsed from APP_MODULE_EVAL to APP_COMPONENT_INIT: ${elapsedMs}ms`,
      { elapsedMs, renderThreadBlocked: elapsedMs > 100 }
    );
  } else {
    debugLog('PERFORMANCE_TRACE', '[Performance Trace] APP_COMPONENT_INIT reached');
  }

  debugLog('APP_COMPONENT_INIT', 'Initializing App component state and hooks');
}

export function logRootInit(rootElementFound: boolean) {
  debugLog(
    'ROOT_INIT',
    rootElementFound
      ? 'React root DOM node (#root) located successfully. Initializing createRoot.'
      : 'CRITICAL ERROR: React root DOM node (#root) NOT found in document!'
  );
}

export function logMount(componentName: string, state?: any) {
  debugLog('COMPONENT_MOUNT', `${componentName} mounted into DOM tree`, state);
}

export function logLazyLoad(
  viewName: string,
  status: 'start' | 'success' | 'error',
  error?: any
) {
  if (status === 'start') {
    debugLog('LAZY_IMPORT_START', `Initiating lazy chunk import for ${viewName}`);
  } else if (status === 'success') {
    debugLog('LAZY_IMPORT_SUCCESS', `Successfully loaded component module for ${viewName}`);
  } else {
    debugLog(
      'LAZY_IMPORT_ERROR',
      `Failed to load component chunk for ${viewName}`,
      error
    );
  }
}

